import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Newspaper, RefreshCw, ExternalLink, Clock, TrendingUp, 
  TrendingDown, Minus, AlertCircle, Globe
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NewsPanel = () => {
  const [symbol, setSymbol] = useState('');
  const [loading, setLoading] = useState(false);
  const [newsData, setNewsData] = useState(null);
  const [error, setError] = useState(null);

  const fetchNews = async () => {
    if (!symbol.trim()) {
      toast.error('Please enter a stock symbol');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API}/data/news/${symbol.toUpperCase()}`);
      setNewsData(response.data);
      
      const count = response.data.news?.length || 0;
      toast.success(`Found ${count} news articles for ${symbol.toUpperCase()}`);
    } catch (err) {
      console.error('Error fetching news:', err);
      setError(err.response?.data?.detail || 'Failed to fetch news');
      toast.error('Failed to fetch news');
    } finally {
      setLoading(false);
    }
  };

  const getSentimentBadge = (sentiment) => {
    if (!sentiment) return null;
    
    const label = sentiment.label || sentiment;
    const lowerLabel = label.toLowerCase();
    
    if (lowerLabel.includes('bullish') || lowerLabel.includes('positive')) {
      return (
        <Badge className="bg-green-100 text-green-700 text-[10px]">
          <TrendingUp className="h-3 w-3 mr-1" />
          Bullish
        </Badge>
      );
    } else if (lowerLabel.includes('bearish') || lowerLabel.includes('negative')) {
      return (
        <Badge className="bg-red-100 text-red-700 text-[10px]">
          <TrendingDown className="h-3 w-3 mr-1" />
          Bearish
        </Badge>
      );
    } else {
      return (
        <Badge className="bg-gray-100 text-gray-700 text-[10px]">
          <Minus className="h-3 w-3 mr-1" />
          Neutral
        </Badge>
      );
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffHrs < 1) return 'Just now';
      if (diffHrs < 24) return `${diffHrs}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  const getSourceColor = (source) => {
    const colors = {
      'yahoo': 'bg-purple-100 text-purple-700',
      'finnhub': 'bg-blue-100 text-blue-700',
      'polygon': 'bg-green-100 text-green-700',
      'alphavantage': 'bg-orange-100 text-orange-700',
    };
    return colors[source] || 'bg-gray-100 text-gray-700';
  };

  return (
    <Card className="glass-effect border-0 shadow-lg">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center text-lg">
          <Newspaper className="h-5 w-5 mr-2 text-blue-600" />
          Stock News
        </CardTitle>
        <CardDescription>
          Latest news and sentiment analysis
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Search Input */}
        <div className="flex space-x-2">
          <Input
            placeholder="Enter symbol (e.g., AAPL)"
            value={symbol}
            onChange={(e) => setSymbol(e.target.value.toUpperCase())}
            onKeyPress={(e) => e.key === 'Enter' && fetchNews()}
            className="flex-1"
          />
          <Button 
            onClick={fetchNews} 
            disabled={loading}
            className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600"
          >
            {loading ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Newspaper className="h-4 w-4" />
            )}
          </Button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center">
            <AlertCircle className="h-4 w-4 mr-2" />
            {error}
          </div>
        )}

        {/* News Results */}
        {newsData && (
          <div className="space-y-3">
            {/* Stats */}
            <div className="flex items-center justify-between text-xs text-gray-500 px-1">
              <span>{newsData.total_count} articles found</span>
              <div className="flex items-center space-x-2">
                {Object.entries(newsData.sources || {}).map(([src, count]) => (
                  count > 0 && (
                    <Badge key={src} variant="outline" className={`text-[10px] ${getSourceColor(src)}`}>
                      {src}: {count}
                    </Badge>
                  )
                ))}
              </div>
            </div>

            {/* News List */}
            <ScrollArea className="h-[400px] pr-2">
              <div className="space-y-3">
                {newsData.news?.map((article, index) => (
                  <a
                    key={index}
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-3 bg-white rounded-lg border border-gray-100 hover:border-blue-200 hover:shadow-md transition-all group"
                  >
                    <div className="flex items-start space-x-3">
                      {/* Thumbnail */}
                      {article.thumbnail && (
                        <img 
                          src={article.thumbnail} 
                          alt="" 
                          className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      )}
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900 line-clamp-2 group-hover:text-blue-600 transition-colors">
                          {article.title}
                        </h4>
                        
                        {article.summary && (
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                            {article.summary}
                          </p>
                        )}
                        
                        <div className="flex items-center space-x-2 mt-2">
                          <Badge variant="outline" className={`text-[10px] ${getSourceColor(article.source_api)}`}>
                            {article.source}
                          </Badge>
                          
                          {article.sentiment && getSentimentBadge(article.sentiment)}
                          {article.overall_sentiment && getSentimentBadge(article.overall_sentiment)}
                          
                          <span className="text-[10px] text-gray-400 flex items-center">
                            <Clock className="h-3 w-3 mr-1" />
                            {formatDate(article.published)}
                          </span>
                          
                          <ExternalLink className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    </div>
                  </a>
                ))}
                
                {newsData.news?.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Newspaper className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                    <p className="text-sm">No news found for {newsData.symbol}</p>
                  </div>
                )}
              </div>
            </ScrollArea>

            {/* Cached indicator */}
            {newsData.cached && (
              <p className="text-xs text-center text-amber-600">
                Showing cached results
              </p>
            )}
          </div>
        )}

        {/* Instructions */}
        {!newsData && !loading && (
          <div className="text-center py-6 text-gray-500">
            <Globe className="h-8 w-8 mx-auto mb-2 text-gray-400" />
            <p className="text-sm">Enter a symbol to fetch latest news</p>
            <p className="text-xs mt-1">Sources: Yahoo Finance, Finnhub, Polygon, Alpha Vantage</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NewsPanel;
